import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { forkJoin } from 'rxjs';
import { SharedService } from '../../services/shared.service';
import { InvoiceService } from '../../services/invoice.service';
import { PaymentLinksService } from '../../services/payment-links.service';
import { ActivatedRoute, Router } from '@angular/router';
import { PatientService } from '../../services/patient.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-invoice-profile',
  templateUrl: './invoice-profile.component.html',
  styleUrls: ['./invoice-profile.component.css']
})
export class InvoiceProfileComponent implements OnInit, AfterViewInit {
  @ViewChild('patientNameInput', { static: false }) patientNameInput: ElementRef;
  showErrorMessage: boolean;
  errorMessage: string;
  showLoadingMessage = false;

  /** accounts | payments */
  activeFinanceTab = 'accounts';

  /** Full list from API (active + inactive), each item has isActive */
  allPatientDtos: any[] = [];
  residentName = '';
  /** Client-side filter: substring match on patient id (numeric id as string) */
  patientIdFilter = '';
  /** When true: show only inactive; when false: show only active */
  showOnlyInactive = false;

  /** Payments tab: selected patient ids for bulk email */
  paymentSelectedMap: { [patientId: number]: boolean } = {};

  /** Last successful payment-setup-link email (Toronto local), keyed by patientId */
  paymentLastEmailSentMap: { [patientId: number]: string } = {};

  /** Card / bank counts from SolvoPayment, keyed by patientId */
  paymentMethodsSummaryMap: {
    [patientId: number]: { found: boolean; cardCount: number; bankAccountCount: number }
  } = {};
  paymentMethodsSummaryLoading = false;

  constructor(
    private sharedService: SharedService,
    private router: Router,
    private route: ActivatedRoute,
    private patientService: PatientService,
    private invoiceService: InvoiceService,
    private paymentLinksService: PaymentLinksService
  ) {}

  ngOnInit(): void {
    this.sharedService.reloadUserPrivileges();
    this.restoreFinanceTabFromQuery();
    const self = this;
    this.route.queryParams.subscribe(function (qp) {
      const tabId = self.getFinanceTabIdFromQuery(qp['tab']);
      if (tabId) {
        self.activeFinanceTab = tabId;
      }
    });
    this.loadAllPatientDtos();
  }

  ngAfterViewInit(): void {
    if (this.patientNameInput && this.patientNameInput.nativeElement) {
      this.patientNameInput.nativeElement.focus();
    }
  }

  selectFinanceTab(tabId: string): void {
    if (tabId !== 'accounts' && tabId !== 'payments') {
      return;
    }
    this.activeFinanceTab = tabId;
    this.syncFinanceTabQueryParam(tabId);
  }

  private restoreFinanceTabFromQuery(): void {
    const tabId = this.getFinanceTabIdFromQuery(this.route.snapshot.queryParamMap.get('tab'));
    if (tabId) {
      this.activeFinanceTab = tabId;
    }
  }

  private getFinanceTabIdFromQuery(queryValue: string): string | null {
    if (!queryValue) {
      return null;
    }
    const normalized = String(queryValue).trim().toLowerCase();
    if (normalized === 'payments') {
      return 'payments';
    }
    if (normalized === 'accounts') {
      return 'accounts';
    }
    return null;
  }

  private syncFinanceTabQueryParam(tabId: string): void {
    const queryParams: any = {
      tab: tabId === 'payments' ? 'payments' : null
    };
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  /** Load active and inactive lists once, merge with isActive flag */
  loadAllPatientDtos(): void {
    this.showLoadingMessage = true;
    const active$ = this.patientService.getPatientDtos(
      this.sharedService.pharmacyId,
      this.sharedService.facilityId,
      this.sharedService.getUserId(),
      true
    );
    const inactive$ = this.patientService.getPatientDtos(
      this.sharedService.pharmacyId,
      this.sharedService.facilityId,
      this.sharedService.getUserId(),
      false
    );
    forkJoin([active$, inactive$]).subscribe({
      next: ([activeList, inactiveList]) => {
        activeList.forEach((p: any) => (p.isActive = true));
        inactiveList.forEach((p: any) => (p.isActive = false));
        this.allPatientDtos = [...(activeList || []), ...(inactiveList || [])];
        this.paymentSelectedMap = {};
        this.showLoadingMessage = false;
        this.loadPaymentSetupLinkEmailLastSent();
        this.loadPaymentMethodsSummaries();
      },
      error: () => (this.showLoadingMessage = false)
    });
  }

  loadPaymentMethodsSummaries(): void {
    const self = this;
    const ids: number[] = [];
    for (let i = 0; i < this.allPatientDtos.length; i++) {
      const id = this.allPatientDtos[i] && this.allPatientDtos[i].id;
      if (id != null && !isNaN(Number(id))) {
        ids.push(Number(id));
      }
    }
    if (ids.length === 0) {
      this.paymentMethodsSummaryMap = {};
      return;
    }

    this.paymentMethodsSummaryLoading = true;
    this.invoiceService.queryPatientPaymentMethodsSummary({
      pharmacyId: this.sharedService.pharmacyId,
      patientIds: ids
    }).subscribe(
      function (result) {
        self.paymentMethodsSummaryLoading = false;
        const map: {
          [patientId: number]: { found: boolean; cardCount: number; bankAccountCount: number }
        } = {};
        const items = result && result.success && result.data ? result.data : [];
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item && item.patientId != null) {
            map[item.patientId] = {
              found: !!item.found,
              cardCount: item.cardCount != null ? Number(item.cardCount) : 0,
              bankAccountCount: item.bankAccountCount != null ? Number(item.bankAccountCount) : 0
            };
          }
        }
        self.paymentMethodsSummaryMap = map;
      },
      function () {
        self.paymentMethodsSummaryLoading = false;
        self.paymentMethodsSummaryMap = {};
      }
    );
  }

  loadPaymentSetupLinkEmailLastSent(): void {
    const self = this;
    const ids: number[] = [];
    for (let i = 0; i < this.allPatientDtos.length; i++) {
      const id = this.allPatientDtos[i] && this.allPatientDtos[i].id;
      if (id != null && !isNaN(Number(id))) {
        ids.push(Number(id));
      }
    }
    if (ids.length === 0) {
      this.paymentLastEmailSentMap = {};
      return;
    }

    this.invoiceService.getPaymentSetupLinkEmailLastSent({
      pharmacyId: this.sharedService.pharmacyId,
      facilityId: this.sharedService.facilityId,
      patientIds: ids
    }).subscribe(
      function (result) {
        const map: { [patientId: number]: string } = {};
        const items = result && result.success && result.data ? result.data : [];
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item && item.patientId != null && item.lastEmailSentAt) {
            map[item.patientId] = String(item.lastEmailSentAt);
          }
        }
        self.paymentLastEmailSentMap = map;
      },
      function () {
        self.paymentLastEmailSentMap = {};
      }
    );
  }

  /** Client-side filtered list: inactive/active, Patient Name, Patient ID (substring on id) */
  get filteredPatientDtos(): any[] {
    if (!this.allPatientDtos || this.allPatientDtos.length === 0) {
      return [];
    }
    let list = this.showOnlyInactive
      ? this.allPatientDtos.filter((p) => !p.isActive)
      : this.allPatientDtos.filter((p) => p.isActive);
    const term = (this.residentName || '').trim().toLowerCase();
    if (term) {
      list = list.filter((p) => {
        const fullName = ((p.lastName || '') + ' ' + (p.firstName || '')).toLowerCase();
        const fullNameReversed = ((p.firstName || '') + ' ' + (p.lastName || '')).toLowerCase();
        return fullName.indexOf(term) !== -1 || fullNameReversed.indexOf(term) !== -1;
      });
    }
    const idTerm = (this.patientIdFilter || '').trim();
    if (idTerm) {
      list = list.filter((p) => {
        const idStr = p.id != null ? String(p.id) : '';
        return idStr.indexOf(idTerm) !== -1;
      });
    }
    return list;
  }

  isPaymentSelected(patientId: number): boolean {
    return !!this.paymentSelectedMap[patientId];
  }

  togglePaymentSelected(patientId: number, checked: boolean): void {
    if (checked) {
      this.paymentSelectedMap[patientId] = true;
    } else {
      delete this.paymentSelectedMap[patientId];
    }
  }

  onPaymentCheckboxChange(patientId: number, event: any): void {
    const checked = !!(event && event.target && event.target.checked);
    this.togglePaymentSelected(patientId, checked);
  }

  get selectedPaymentCount(): number {
    return Object.keys(this.paymentSelectedMap).length;
  }

  get allVisiblePaymentsSelected(): boolean {
    const list = this.filteredPatientDtos;
    if (!list || list.length === 0) {
      return false;
    }
    for (let i = 0; i < list.length; i++) {
      if (!this.isPaymentSelected(list[i].id)) {
        return false;
      }
    }
    return true;
  }

  toggleSelectAllPayments(checked: boolean): void {
    const list = this.filteredPatientDtos;
    for (let i = 0; i < list.length; i++) {
      this.togglePaymentSelected(list[i].id, checked);
    }
  }

  onSelectAllPaymentsChange(event: any): void {
    const checked = !!(event && event.target && event.target.checked);
    this.toggleSelectAllPayments(checked);
  }

  onPaymentCheckCellClick(event: any, patientId: number): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.togglePaymentSelected(patientId, !this.isPaymentSelected(patientId));
  }

  /**
   * Card count for Payments tab. Number only when registered; empty when none.
   */
  getPaymentCardCountDisplay(patient: any): string {
    if (!patient || patient.id == null) {
      return '';
    }
    if (this.paymentMethodsSummaryLoading && !this.paymentMethodsSummaryMap[patient.id]) {
      return '';
    }
    const summary = this.paymentMethodsSummaryMap[patient.id];
    if (!summary) {
      return '';
    }
    const cards = summary.cardCount != null ? summary.cardCount : 0;
    return cards > 0 ? String(cards) : '';
  }

  /**
   * Bank account count for Payments tab. Number only when registered; empty when none.
   */
  getPaymentBankCountDisplay(patient: any): string {
    if (!patient || patient.id == null) {
      return '';
    }
    if (this.paymentMethodsSummaryLoading && !this.paymentMethodsSummaryMap[patient.id]) {
      return '';
    }
    const summary = this.paymentMethodsSummaryMap[patient.id];
    if (!summary) {
      return '';
    }
    const banks = summary.bankAccountCount != null ? summary.bankAccountCount : 0;
    return banks > 0 ? String(banks) : '';
  }

  /**
   * Last successful payment setup link email from UserActionLog (or —).
   */
  getLastEmailSentText(patient: any): string {
    if (!patient || patient.id == null) {
      return '—';
    }
    const raw = this.paymentLastEmailSentMap[patient.id];
    if (!raw) {
      return '—';
    }
    const text = String(raw);
    // Stored as Toronto local; prefer string slice to avoid browser TZ shifts.
    if (text.indexOf('T') > 0 && text.length >= 16) {
      return text.substring(0, 10) + ' ' + text.substring(11, 16);
    }
    const d = new Date(text);
    if (isNaN(d.getTime())) {
      return '—';
    }
    const pad = function (n: number) {
      return n < 10 ? '0' + n : String(n);
    };
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
      + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
  }

  emailSelectedPaymentSetupLinks(): void {
    const patientIds = this.getSelectedPaymentPatientIds();
    if (!patientIds || patientIds.length === 0) {
      return;
    }

    const self = this;
    Swal.fire({
      title: 'Preparing...',
      allowOutsideClick: false,
      allowEscapeKey: false,
      onOpen: function () {
        Swal.showLoading();
      }
    });

    this.invoiceService.getPaymentVerifyLinkEmailBatchOptions({
      pharmacyId: this.sharedService.pharmacyId,
      facilityId: this.sharedService.facilityId,
      patientIds: patientIds
    }).subscribe(
      function (result) {
        if (!result || !result.success || !result.data) {
          const msg = (result && result.data) ? String(result.data) : 'Failed to load email recipients.';
          Swal.fire({ icon: 'error', title: 'Unable to prepare email', text: msg, confirmButtonText: 'OK' });
          return;
        }

        const recipients = result.data.recipients || [];
        const sendable = recipients.filter(function (r) { return !!r.canSend; });
        const skipped = recipients.filter(function (r) { return !r.canSend; });

        if (sendable.length === 0) {
          Swal.fire({
            icon: 'warning',
            title: 'No recipients ready',
            html: self.buildRecipientPreviewHtml(recipients, result.data.primaryRecipientLabel)
              + '<p class="mt-2 mb-0 text-muted">Fix missing emails or access keys, then try again.</p>',
            width: 720,
            confirmButtonText: 'OK'
          });
          return;
        }

        const subjectDefault = result.data.emailSubject
          || 'Please set up your payment method — Advantage Care Pharmacy';
        const bodyPreview = (result.data.emailBody || '')
          .replace(/\{\{PAYMENT_REGISTER_URL\}\}/g, '#');

        Swal.fire({
          icon: 'question',
          title: 'Send Email Setup Link?',
          width: 800,
          html: `
            <div style="text-align:left;">
              <p class="text-muted mb-2">
                A secure payment method setup link will be emailed to each recipient below.
                Emails are sent from the invoice billing address.
              </p>
              <div class="mb-2 fw-bold">Recipients (${sendable.length} will be sent`
                + (skipped.length > 0 ? `, ${skipped.length} skipped` : '')
                + `)</div>
              ${self.buildRecipientPreviewHtml(recipients, result.data.primaryRecipientLabel)}
              <hr class="my-3" />
              <div class="mb-2 fw-bold">Email content (preview)</div>
              <div style="border:1px solid #dee2e6;border-radius:6px;padding:12px 14px;background:#f8f9fa;">
                <div style="font-size:12px;color:#6c757d;margin-bottom:4px;">Subject</div>
                <div style="font-weight:600;margin-bottom:12px;">${self.escapeHtml(subjectDefault)}</div>
                <div style="font-size:12px;color:#6c757d;margin-bottom:4px;">Body</div>
                <div style="background:#fff;border:1px solid #e9ecef;border-radius:4px;padding:12px;max-height:280px;overflow:auto;">
                  ${bodyPreview}
                </div>
                <div class="text-muted mt-2" style="font-size:12px;">
                  Name placeholders: {FirstName} {LastName}. Each email replaces them with that patient&apos;s name,
                  and the blue button becomes a unique setup link.
                </div>
              </div>
            </div>
          `,
          showCancelButton: true,
          cancelButtonText: 'Cancel',
          confirmButtonText: 'Send (' + sendable.length + ')',
          preConfirm: function () {
            return { recipients: sendable };
          }
        }).then(function (swalResult) {
          if (!swalResult || !swalResult.isConfirmed || !swalResult.value) {
            return;
          }
          self.sendSetupLinkEmailsSequentially(swalResult.value.recipients);
        });
      },
      function (err) {
        const msg = err && err.error && err.error.data
          ? String(err.error.data)
          : 'Failed to load email recipients.';
        Swal.fire({ icon: 'error', title: 'Unable to prepare email', text: msg, confirmButtonText: 'OK' });
      }
    );
  }

  private getSelectedPaymentPatientIds(): number[] {
    const ids: number[] = [];
    const keys = Object.keys(this.paymentSelectedMap);
    for (let i = 0; i < keys.length; i++) {
      const id = Number(keys[i]);
      if (!isNaN(id) && this.paymentSelectedMap[id]) {
        ids.push(id);
      }
    }
    return ids;
  }

  private buildRecipientPreviewHtml(recipients: any[], primaryLabel: string): string {
    if (!recipients || recipients.length === 0) {
      return '<p class="text-muted mb-0">No recipients.</p>';
    }
    const label = this.escapeHtml(primaryLabel || 'Email');
    let rows = '';
    for (let i = 0; i < recipients.length; i++) {
      const r = recipients[i];
      const name = this.escapeHtml(r.listDisplayName || ((r.lastName || '') + ', ' + (r.firstName || '')));
      const email = r.patientEmail
        ? this.escapeHtml(r.patientEmail)
        : '<span class="text-muted">(not available)</span>';
      const status = r.canSend
        ? '<span class="text-success">Ready</span>'
        : '<span class="text-danger">' + this.escapeHtml(r.skipReason || 'Skipped') + '</span>';
      rows += '<tr>'
        + '<td style="padding:6px 8px;">' + name + '</td>'
        + '<td style="padding:6px 8px;">' + email + '</td>'
        + '<td style="padding:6px 8px;">' + status + '</td>'
        + '</tr>';
    }
    return '<div style="max-height:220px;overflow:auto;border:1px solid #dee2e6;border-radius:6px;">'
      + '<table class="table table-sm mb-0" style="font-size:13px;">'
      + '<thead><tr>'
      + '<th style="padding:6px 8px;">Patient ({LastName}, {FirstName})</th>'
      + '<th style="padding:6px 8px;">' + label + '</th>'
      + '<th style="padding:6px 8px;">Status</th>'
      + '</tr></thead><tbody>' + rows + '</tbody></table></div>';
  }

  private sendSetupLinkEmailsSequentially(recipients: any[]): void {
    const self = this;
    const queue = (recipients || []).slice();
    let sent = 0;
    let failed = 0;
    const failures: string[] = [];

    const updateProgress = function (current: number, total: number) {
      Swal.fire({
        title: 'Sending emails...',
        html: '<p class="mb-0">Sending ' + current + ' of ' + total + '</p>',
        allowOutsideClick: false,
        allowEscapeKey: false,
        onOpen: function () {
          Swal.showLoading();
        }
      });
    };

    const finish = function () {
      self.loadPaymentSetupLinkEmailLastSent();
      let html = '<p><strong>Sent:</strong> ' + sent + '</p>'
        + '<p><strong>Failed:</strong> ' + failed + '</p>';
      if (failures.length > 0) {
        html += '<div style="text-align:left;max-height:180px;overflow:auto;"><ul class="mb-0">';
        for (let i = 0; i < failures.length; i++) {
          html += '<li>' + self.escapeHtml(failures[i]) + '</li>';
        }
        html += '</ul></div>';
      }
      Swal.fire({
        icon: failed === 0 ? 'success' : (sent > 0 ? 'warning' : 'error'),
        title: failed === 0 ? 'Emails sent' : 'Send finished with errors',
        html: html,
        confirmButtonText: 'OK'
      });
    };

    const sendNext = function (index: number) {
      if (index >= queue.length) {
        finish();
        return;
      }

      const recipient = queue[index];
      const displayName = recipient.listDisplayName
        || ((recipient.lastName || '') + ', ' + (recipient.firstName || ''));
      updateProgress(index + 1, queue.length);

      const accessKey = recipient.accessKey ? String(recipient.accessKey) : '';
      if (!accessKey) {
        failed++;
        failures.push(displayName + ': access key missing');
        sendNext(index + 1);
        return;
      }

      self.paymentLinksService.generate(accessKey).subscribe(
        function (generateResult) {
          const setupLink = generateResult && generateResult.paymentMethodSetupLink
            ? String(generateResult.paymentMethodSetupLink)
            : '';
          if (!setupLink) {
            failed++;
            failures.push(displayName + ': failed to create setup link');
            sendNext(index + 1);
            return;
          }

          self.invoiceService.sendPaymentVerifyLinkEmail({
            pharmacyId: self.sharedService.pharmacyId,
            facilityId: self.sharedService.facilityId,
            patientId: recipient.patientId,
            recipientMode: 'patient',
            paymentMethodSetupLink: setupLink,
            performedByUserId: self.sharedService.getUserId()
          }).subscribe(
            function (sendResult) {
              if (!sendResult || !sendResult.success) {
                failed++;
                const msg = (sendResult && sendResult.data)
                  ? String(sendResult.data)
                  : 'send failed';
                failures.push(displayName + ': ' + msg);
              } else {
                sent++;
              }
              sendNext(index + 1);
            },
            function (err) {
              failed++;
              const msg = err && err.error && err.error.data
                ? String(err.error.data)
                : 'send failed';
              failures.push(displayName + ': ' + msg);
              sendNext(index + 1);
            }
          );
        },
        function (err) {
          failed++;
          const msg = err && err.error && err.error.message
            ? String(err.error.message)
            : 'failed to create setup link';
          failures.push(displayName + ': ' + msg);
          sendNext(index + 1);
        }
      );
    };

    sendNext(0);
  }

  private escapeHtml(value: string): string {
    if (!value) {
      return '';
    }
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  openProfile(patientId: number): void {
    const url = 'invoice/profile/' + this.sharedService.pharmacyId + '/'
      + this.sharedService.facilityId + '/' + patientId;
    this.router.navigate([url]);
  }

  openProfilePayments(patientId: number): void {
    const url = 'invoice/profile/' + this.sharedService.pharmacyId + '/'
      + this.sharedService.facilityId + '/' + patientId;
    this.router.navigate([url], {
      queryParams: {
        tab: 'payments',
        listTab: 'payments'
      }
    });
  }

  clearErrorMessage(): void {
    this.showErrorMessage = false;
    this.errorMessage = '';
  }
}
